import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllTestimonials } from "../redux/actions/contents";
import useAuth from "../useAuth";
import PageName from "../Components/global/PageName";
import { Rating } from "@mui/material";
import { FaCheckCircle, FaTimesCircle, FaQuoteLeft, FaPhoneAlt } from "react-icons/fa";

export default function TestimonialManager() {
  const dispatch = useDispatch();
  const { testimonialsList, loading: testimonialsLoading } = useSelector(
    (state) => state.content
  );
  const { clientInfo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllTestimonials(1, 100, clientInfo?.clientId));
  }, [sync, dispatch, clientInfo]);

  const handleUpdateVerifyStatus = async (id, verifiedStatus) => {
    const userRes = window.confirm(
      `Are you sure you want to ${
        verifiedStatus ? "Un-Verify" : "Verify"
      } this review?`
    );
    if (!userRes) return null;

    try {
      setLoading(true);
      const { data: res } = await axios.post(
        `${server}/testimonial/updateVerifyStatus`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            testimonialId: id,
            verifyStatus: !verifiedStatus,
          },
        }
      );
      toast.success(verifiedStatus ? "Review un-verified" : "Review verified");
      setSync((prv) => prv + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12">
      <PageName
        title="Testimonials & Reviews Manager"
        subTitle="Verify, manage and feature client reviews on the main site."
      />

      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="overflow-x-auto rounded-2xl shadow-sm border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-6 py-4 text-left">Client Avatar</th>
                <th className="px-6 py-4 text-left">Client Name & Mobile</th>
                <th className="px-6 py-4 text-left">Review Quote</th>
                <th className="px-6 py-4 text-center">Rating</th>
                <th className="px-6 py-4 text-center">Verification Status</th>
                <th className="px-6 py-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {testimonialsList?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-slate-400">
                    No client reviews found.
                  </td>
                </tr>
              ) : (
                testimonialsList?.map((item, index) => (
                  <motion.tr
                    key={item?.id || index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="hover:bg-amber-50/40 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="w-10 h-10 rounded-full bg-amber-100 border border-amber-300 text-amber-700 flex items-center justify-center font-bold text-sm">
                        {item?.name?.[0]?.toUpperCase() || "C"}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      <div>{item?.name || "Anonymous Client"}</div>
                      <div className="flex items-center gap-1 text-slate-500 font-normal mt-0.5">
                        <FaPhoneAlt size={10} className="text-amber-600" />
                        <span>{item?.mobile || "N/A"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 max-w-md">
                      <div className="flex items-start gap-2">
                        <FaQuoteLeft className="text-amber-500/30 flex-shrink-0 mt-0.5" size={14} />
                        <p className="italic text-slate-700 font-serif leading-relaxed">
                          "{item?.review || "No review content provided."}"
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Rating value={item?.rating || 5} readOnly size="small" />
                    </td>
                    <td className="px-6 py-4 text-center font-semibold">
                      {item?.verified ? (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                          <FaCheckCircle size={11} /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                          <FaTimesCircle size={11} /> Un-Verified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() =>
                          handleUpdateVerifyStatus(item?.id, item?.verified)
                        }
                        className={`text-xs text-white px-4 py-2 rounded-xl font-semibold tracking-wider uppercase transition-all shadow-sm ${
                          item?.verified
                            ? "bg-rose-600 hover:bg-rose-700 shadow-rose-600/20"
                            : "bg-green-600 hover:bg-green-700 shadow-green-600/20"
                        }`}
                      >
                        {!item?.verified ? "Verify" : "Un-Verify"}
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(loading || testimonialsLoading) && <Loading />}
    </div>
  );
}
